import { useState } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { Plus, Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Link } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import {
  useKnowledgeBases,
  useBulkDeleteKnowledgeBases,
  KnowledgeBase,
} from '@/hooks/useKnowledgeBases'
import { DataTable } from '@/components/common/DataTable'
import { KBEmptyState } from '@/components/knowledge/KBEmptyState'
import { useToast } from '@/hooks/use-toast'

const columns: ColumnDef<KnowledgeBase, any>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
    cell: ({ row }) => (
      <Link
        to={`/app/knowledge/${row.original.id}`}
        className="font-medium text-primary hover:underline"
      >
        {row.original.name}
      </Link>
    ),
  },
  {
    accessorKey: 'description',
    header: 'Description',
    cell: ({ row }) => (
      <span className="text-muted-foreground line-clamp-1 max-w-[300px]">
        {row.original.description || '—'}
      </span>
    ),
  },
  {
    accessorKey: 'visibility',
    header: 'Access',
    cell: ({ row }) => (
      <Badge variant={row.original.visibility === 'public' ? 'default' : 'secondary'}>
        {row.original.visibility}
      </Badge>
    ),
  },
  {
    accessorKey: 'document_count',
    header: 'Docs',
    cell: ({ row }) => row.original.document_count,
  },
  {
    accessorKey: 'created_at',
    header: 'Created',
    cell: ({ row }) =>
      formatDistanceToNow(new Date(row.original.created_at), { addSuffix: true }),
  },
]

export function KnowledgeBaseList() {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [search, setSearch] = useState('')

  const { data, isLoading } = useKnowledgeBases({ page, pageSize, search })
  const bulkDelete = useBulkDeleteKnowledgeBases()
  const { toast } = useToast()

  const handleBulkDelete = (ids: string[]) => {
    bulkDelete.mutate(ids, {
      onSuccess: (res) => {
        toast({ title: `Deleted ${res.deleted_count} knowledge base(s)` })
      },
      onError: () => {
        toast({ title: 'Failed to delete', variant: 'destructive' })
      },
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Knowledge Bases</h1>
          <p className="text-muted-foreground mt-2">
            Manage your organization's knowledge repositories
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" asChild>
            <Link to="/app/knowledge/import">
              <Download className="h-4 w-4 mr-2" />
              Import from RAGflow
            </Link>
          </Button>
          <Button asChild>
            <Link to="/app/knowledge/create">
              <Plus className="h-4 w-4 mr-2" />
              Create Knowledge Base
            </Link>
          </Button>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={data?.items ?? []}
        total={data?.total ?? 0}
        page={page}
        pageSize={pageSize}
        search={search}
        isLoading={isLoading}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onSearchChange={setSearch}
        onBulkDelete={handleBulkDelete}
        isBulkDeleting={bulkDelete.isPending}
        getRowId={(row) => row.id}
        emptyState={<KBEmptyState />}
      />
    </div>
  )
}
